import React from 'react';

import AIModel from 'types/ai_model';

import {
  useAdminMarketplaceModels,
  useMarketplaceModels,
} from 'hooks/api/useModels';

import * as PageLayout from 'components/PageLayout';
import { AdvancedSearch } from 'components/AdvancedSearch/AdvancedSearch';
import { ModelGrid } from 'analytics-library/components/ModelGrid/ModelGrid';
import {
  CatalogScope,
  CatalogScopeSwitch,
} from 'analytics-library/components/CatalogScopeSwitch/CatalogScopeSwitch';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';

const EMPTY_MODELS: AIModel[] = [];

type MarketplaceProps = {
  isAdminView?: boolean;
};

// Ready-to-use AI Models
export function Marketplace({ isAdminView = false }: MarketplaceProps) {
  const [manualPlanScope, setManualPlanScope] = React.useState<CatalogScope>();
  const [searchInput, setSearchInput] = React.useState('');
  const { isRingPlan } = useCurrentPlan();
  const hasSearchInput = Boolean(searchInput.trim());
  const planScope: CatalogScope = isRingPlan
    ? 'included'
    : (manualPlanScope ?? (hasSearchInput ? 'all' : 'included'));
  const onlyIncludedInPlan = isRingPlan || planScope === 'included';

  function handlePlanScopeChange(value: CatalogScope) {
    setManualPlanScope(value);
  }

  const appScopedMarketplaceModels = useMarketplaceModels({
    params: {
      only_included_in_plan: onlyIncludedInPlan,
    },
    enabled: !isAdminView,
  });
  const adminMarketplaceModels = useAdminMarketplaceModels({
    enabled: isAdminView,
  });
  const { data: marketplaceModels, isLoading } = isAdminView
    ? adminMarketplaceModels
    : appScopedMarketplaceModels;
  const planScopeFilter =
    !isAdminView && !isRingPlan ? (
      <CatalogScopeSwitch
        id="marketplace_plan_scope"
        includedLabel="Included models only"
        allLabel="All models"
        value={planScope}
        onChange={handlePlanScopeChange}
      />
    ) : undefined;

  return (
    <PageLayout.Root>
      <AdvancedSearch
        data={marketplaceModels || EMPTY_MODELS}
        isLoading={isLoading}
        searchLabel="Search models"
        entity="models"
        actions={planScopeFilter}
        onSearchInputChange={!isAdminView ? setSearchInput : undefined}
        searchOnInputChange
      >
        <ModelGrid isMarketplace />
      </AdvancedSearch>
    </PageLayout.Root>
  );
}
